/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useCallback, useEffect, useRef } from 'react';

interface DraggableImageProps {
  imageSrc: string;
  width: number;
  bottom: number;
  right: number;
  opacity: number;
  name: string;
  rotation: number;
  horizontalFlip?: boolean;
  clipBoundsRef?: React.RefObject<HTMLElement>;
  onChangeXY: (dx: number, dy: number) => void;
  onChangeWidth: (newWidth: number) => void;
  onChangeRotation: (newRotation: number) => void;
  onMouseDown: () => void;
}

const DraggableImage: React.FC<DraggableImageProps> = ({
  imageSrc,
  width,
  bottom,
  right,
  opacity,
  name,
  rotation,
  horizontalFlip,
  clipBoundsRef,
  onChangeXY,
  onChangeWidth,
  onChangeRotation,
  onMouseDown,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [startDragPos, setStartDragPos] = useState({ x: 0, y: 0 });
  const [accumulatedDelta, setAccumulatedDelta] = useState({ dx: 0, dy: 0 });
  const [initialWidth, setInitialWidth] = useState(width);
  const [initialRotation, setInitialRotation] = useState(0);
  const [rotationCenter, setRotationCenter] = useState({ x: 0, y: 0 });
  const [imageHeight, setImageHeight] = useState(0);
  const [imageCenterViewport, setImageCenterViewport] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Update image height and center position when it loads or changes
  useEffect(() => {
    const updateImageMetrics = () => {
      if (imageRef.current) {
        setImageHeight(imageRef.current.clientHeight);
        const rect = imageRef.current.getBoundingClientRect();
        // Use center of the bounding rect, which is stable regardless of rotation
        setImageCenterViewport({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        });
      }
    };

    // Update on load and when image changes
    const img = imageRef.current;
    if (img) {
      if (img.complete) {
        updateImageMetrics();
      }
      img.addEventListener('load', updateImageMetrics);
      return () => img.removeEventListener('load', updateImageMetrics);
    }
  }, [imageSrc, width, bottom, right]);

  // Update center position on scroll and resize
  useEffect(() => {
    const updateCenterPos = () => {
      if (imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        setImageCenterViewport({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        });
      }
    };

    // Update on scroll and resize events
    window.addEventListener('scroll', updateCenterPos, true);
    window.addEventListener('resize', updateCenterPos);

    return () => {
      window.removeEventListener('scroll', updateCenterPos, true);
      window.removeEventListener('resize', updateCenterPos);
    };
  }, []);

  // Handle clicking outside to lose focus
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    if (isFocused) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isFocused]);

  const handleImageMouseDown = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFocused(true);
    setIsDragging(true);
    setStartDragPos({ x: e.clientX, y: e.clientY });
    setAccumulatedDelta({ dx: 0, dy: 0 });
    onMouseDown();
  }, [onMouseDown]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeHandle(handle);
    setStartDragPos({ x: e.clientX, y: e.clientY });
    setInitialWidth(width);
    setAccumulatedDelta({ dx: 0, dy: 0 });
  }, [width, right, bottom]);

  const handleRotateMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Calculate the center of the image in viewport coordinates
    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setRotationCenter({ x: centerX, y: centerY });
    }

    setIsRotating(true);
    setInitialRotation(rotation);
    setStartDragPos({ x: e.clientX, y: e.clientY });
  }, [rotation]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        e.stopPropagation();
        // For dragging, use original convention: positive when moving left/up
        setAccumulatedDelta({
          dx: startDragPos.x - e.clientX,
          dy: startDragPos.y - e.clientY,
        });
      } else if (isResizing) {
        e.stopPropagation();
        // For resizing, use new convention: positive when moving right/down
        const dx = e.clientX - startDragPos.x;
        const dy = e.clientY - startDragPos.y;
        setAccumulatedDelta({ dx, dy });
      } else if (isRotating) {
        e.stopPropagation();
        // Calculate angle from center to current mouse position
        const deltaX = e.clientX - rotationCenter.x;
        const deltaY = e.clientY - rotationCenter.y;
        const angleRad = Math.atan2(deltaY, deltaX);
        const angleDeg = angleRad * (180 / Math.PI);

        // Calculate initial angle
        const initialDeltaX = startDragPos.x - rotationCenter.x;
        const initialDeltaY = startDragPos.y - rotationCenter.y;
        const initialAngleRad = Math.atan2(initialDeltaY, initialDeltaX);
        const initialAngleDeg = initialAngleRad * (180 / Math.PI);

        // Calculate rotation change and snap to 0.5 degree increments
        const rotationChange = angleDeg - initialAngleDeg;
        const snappedRotationChange = Math.round(rotationChange / 0.5) * 0.5;
        setAccumulatedDelta({ dx: snappedRotationChange, dy: 0 });
      }
    },
    [isDragging, isResizing, isRotating, startDragPos, resizeHandle, rotationCenter]
  );

  const handleMouseUp = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    if (isDragging) {
      // Save the delta before resetting
      const finalDx = accumulatedDelta.dx;
      const finalDy = accumulatedDelta.dy;

      // Reset state immediately to prevent double-offset during re-render
      setAccumulatedDelta({ dx: 0, dy: 0 });
      setIsDragging(false);

      // Apply position change after resetting
      onChangeXY(finalDx, finalDy);
    } else if (isResizing && resizeHandle) {
      const dx = e.clientX - startDragPos.x;
      const dy = e.clientY - startDragPos.y;
      const aspectRatio = imageHeight > 0 && width > 0 ? imageHeight / width : 1;

      // Determine which edges are being dragged
      const isLeft = resizeHandle.includes('w');
      const isRight = resizeHandle.includes('e');
      const isTop = resizeHandle.includes('n');
      const isBottom = resizeHandle.includes('s');

      let widthChange = 0;
      let heightChange = 0;
      let finalDx = 0;
      let finalDy = 0;
      let newWidth = width;

      if ((isLeft || isRight) && !(isTop || isBottom)) {
        // Pure horizontal edge
        widthChange = isRight ? dx : -dx;
        newWidth = Math.max(50, initialWidth + widthChange);
        widthChange = newWidth - initialWidth;
        heightChange = widthChange * aspectRatio;

        if (isRight) {
          finalDx = widthChange; // Keep left fixed: decrease right
        }
        finalDy = heightChange / 2; // Keep vertical center: decrease bottom
      } else if ((isTop || isBottom) && !(isLeft || isRight)) {
        // Pure vertical edge
        heightChange = isTop ? -dy : dy;
        const newHeight = Math.max(50 * aspectRatio, imageHeight + heightChange);
        heightChange = newHeight - imageHeight;
        widthChange = heightChange / aspectRatio;
        newWidth = Math.max(50, initialWidth + widthChange);

        if (isBottom) {
          finalDy = heightChange; // Keep top fixed: decrease bottom
        }
        finalDx = widthChange / 2; // Keep horizontal center: decrease right
      } else if ((isLeft || isRight) && (isTop || isBottom)) {
        // Corner: combine horizontal and vertical resize
        const hWidthChange = isRight ? dx : -dx;
        const vHeightChange = isTop ? -dy : dy;
        const vWidthChange = vHeightChange / aspectRatio;

        widthChange = (hWidthChange + vWidthChange) / 2;
        newWidth = Math.max(50, initialWidth + widthChange);
        widthChange = newWidth - initialWidth;
        heightChange = widthChange * aspectRatio;

        if (isRight) {
          finalDx = widthChange; // Keep left fixed: decrease right
        }
        if (isBottom) {
          finalDy = heightChange; // Keep top fixed: decrease bottom
        }
      }

      // Reset state before applying changes to prevent double-offset
      setAccumulatedDelta({ dx: 0, dy: 0 });
      setIsResizing(false);
      setResizeHandle(null);

      // Apply changes after resetting
      // Negate the deltas because preview uses subtraction (bottom - positionDy)
      // but onChangeXY uses addition (bottom + dy)
      onChangeWidth(newWidth);
      onChangeXY(-finalDx, -finalDy);
    } else if (isRotating) {
      // Save the rotation change before resetting
      const rotationChange = accumulatedDelta.dx;

      // Reset state immediately
      setAccumulatedDelta({ dx: 0, dy: 0 });
      setIsRotating(false);

      // Apply rotation after resetting, snapped to 0.5 degree increments
      const finalRotation = initialRotation + rotationChange;
      const snappedRotation = Math.round(finalRotation / 0.5) * 0.5;
      onChangeRotation(snappedRotation);
    }
  }, [isDragging, isResizing, isRotating, accumulatedDelta, onChangeXY, onChangeWidth, onChangeRotation, resizeHandle, startDragPos, initialWidth, initialRotation, imageHeight, width]);

  useEffect(() => {
    if (isDragging || isResizing || isRotating) {
      window.document.addEventListener('mousemove', handleMouseMove);
      window.document.addEventListener('mouseup', handleMouseUp);

      // Update center position during interaction using requestAnimationFrame
      let animationFrameId: number;
      const updatePosition = () => {
        if (imageRef.current) {
          const rect = imageRef.current.getBoundingClientRect();
          setImageCenterViewport({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2
          });
        }
        animationFrameId = requestAnimationFrame(updatePosition);
      };
      animationFrameId = requestAnimationFrame(updatePosition);

      return () => {
        window.document.removeEventListener('mousemove', handleMouseMove);
        window.document.removeEventListener('mouseup', handleMouseUp);
        cancelAnimationFrame(animationFrameId);
      };
    } else {
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
    }
  }, [isDragging, isResizing, isRotating, handleMouseMove, handleMouseUp]);

  if (!imageSrc) {
    return null;
  }

  // Calculate current display width and height during resize
  let displayWidth = width;
  let displayHeight = imageHeight;
  let positionDx = 0;
  let positionDy = 0;
  let displayRotation = rotation;

  if (isResizing && resizeHandle && imageHeight > 0 && width > 0) {
    const dx = accumulatedDelta.dx;
    const dy = accumulatedDelta.dy;
    const aspectRatio = imageHeight / width;

    // Determine which edges are being dragged
    const isLeft = resizeHandle.includes('w');
    const isRight = resizeHandle.includes('e');
    const isTop = resizeHandle.includes('n');
    const isBottom = resizeHandle.includes('s');

    let widthChange = 0;
    let heightChange = 0;

    if (isLeft || isRight) {
      // Horizontal edge movement
      widthChange = isRight ? dx : -dx;
      displayWidth = Math.max(50, initialWidth + widthChange);
      widthChange = displayWidth - initialWidth;
      heightChange = widthChange * aspectRatio;
      displayHeight = displayWidth * aspectRatio;

      // Adjust right position to keep opposite edge fixed
      if (isRight) {
        // Keep left edge fixed: decrease right by width change
        positionDx = widthChange;
      }
      // If isLeft, keep right edge fixed: no right position change

      // Keep vertical center same: decrease bottom by half height change
      positionDy = heightChange / 2;
    }

    if (isTop || isBottom) {
      // Vertical edge movement
      heightChange = isTop ? -dy : dy;
      displayHeight = Math.max(50 * aspectRatio, imageHeight + heightChange);
      heightChange = displayHeight - imageHeight;
      widthChange = heightChange / aspectRatio;
      displayWidth = displayHeight / aspectRatio;

      // Adjust bottom position to keep opposite edge fixed
      if (isBottom) {
        // Keep top edge fixed: decrease bottom by height change
        positionDy = heightChange;
      }
      // If isTop, keep bottom edge fixed: no bottom position change

      // Keep horizontal center same: decrease right by half width change
      positionDx = widthChange / 2;
    }

    // For corners, combine both adjustments
    if ((isLeft || isRight) && (isTop || isBottom)) {
      // Recalculate for corner: use average of horizontal and vertical resize
      const hWidthChange = isRight ? dx : -dx;
      const vHeightChange = isTop ? -dy : dy;
      const vWidthChange = vHeightChange / aspectRatio;

      widthChange = (hWidthChange + vWidthChange) / 2;
      displayWidth = Math.max(50, initialWidth + widthChange);
      widthChange = displayWidth - initialWidth;
      heightChange = widthChange * aspectRatio;
      displayHeight = displayWidth * aspectRatio;

      // Position adjustments for corners
      positionDx = 0;
      positionDy = 0;

      if (isRight) {
        positionDx = widthChange;
      }
      if (isBottom) {
        positionDy = heightChange;
      }
    }
  } else if (isDragging) {
    // For dragging, accumulatedDelta uses original convention
    // Apply as addition (not subtraction)
    positionDx = -accumulatedDelta.dx;
    positionDy = -accumulatedDelta.dy;
  } else if (isRotating) {
    // For rotating, update the display rotation
    displayRotation = rotation + accumulatedDelta.dx;
  }

  // Construct transform string with current rotation and horizontal flip
  const displayTransform = `rotate(${displayRotation}deg) scaleX(${horizontalFlip ? -1 : 1})`;

  // Compute clip-path inset to prevent the overlay from receiving clicks outside the visible container
  const overlayClipPath = (): string | undefined => {
    if (!clipBoundsRef?.current || displayHeight <= 0) return undefined;
    const container = clipBoundsRef.current.getBoundingClientRect();
    const overlayTop    = imageCenterViewport.y - displayHeight / 2;
    const overlayBottom = imageCenterViewport.y + displayHeight / 2;
    const overlayLeft   = imageCenterViewport.x - displayWidth  / 2;
    const overlayRight  = imageCenterViewport.x + displayWidth  / 2;
    const insetTop    = Math.max(0, container.top    - overlayTop);
    const insetBottom = Math.max(0, overlayBottom    - container.bottom);
    const insetLeft   = Math.max(0, container.left   - overlayLeft);
    const insetRight  = Math.max(0, overlayRight     - container.right);
    if (insetTop === 0 && insetBottom === 0 && insetLeft === 0 && insetRight === 0) return undefined;
    return `inset(${insetTop}px ${insetRight}px ${insetBottom}px ${insetLeft}px)`;
  };
  const clipPath = overlayClipPath();

  // Custom rotation cursor - curved bidirectional arrow (raised center line)
  const rotationCursor = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3C!-- Curved arc bowing upward, raised slightly --%3E%3Cpath d='M 4 20 Q 16 14 28 20' stroke='black' stroke-width='3' fill='none'/%3E%3Cpath d='M 4 20 Q 16 14 28 20' stroke='white' stroke-width='1.5' fill='none'/%3E%3C!-- Left arrowhead (rotated -20 degrees) --%3E%3Cpath d='M 4 20 L 7 17 M 4 20 L 7 23' stroke='black' stroke-width='3' fill='none' stroke-linecap='round' transform='rotate(-20 4 20)'/%3E%3Cpath d='M 4 20 L 7 17 M 4 20 L 7 23' stroke='white' stroke-width='1.5' fill='none' stroke-linecap='round' transform='rotate(-20 4 20)'/%3E%3C!-- Right arrowhead (rotated 20 degrees) --%3E%3Cpath d='M 28 20 L 25 17 M 28 20 L 25 23' stroke='black' stroke-width='3' fill='none' stroke-linecap='round' transform='rotate(20 28 20)'/%3E%3Cpath d='M 28 20 L 25 17 M 28 20 L 25 23' stroke='white' stroke-width='1.5' fill='none' stroke-linecap='round' transform='rotate(20 28 20)'/%3E%3C/svg%3E") 16 16, alias`;

  const baseHandleStyle: React.CSSProperties = {
    position: 'absolute',
    backgroundColor: '#4285f4',
    border: '1px solid white',
    pointerEvents: 'auto',
    zIndex: 1,
    borderRadius: '4px',
  };

  // Corner handles are rendered as L-shapes, not single elements
  // (removed cornerHandleStyle - corners will use custom rendering)

  const horizontalEdgeHandleStyle: React.CSSProperties = {
    ...baseHandleStyle,
    width: '40px',
    height: '4px',
  };

  const verticalEdgeHandleStyle: React.CSSProperties = {
    ...baseHandleStyle,
    width: '4px',
    height: '40px',
  };

  const handles = [
    { name: 'nw', top: '-5px', left: '-5px', cursor: 'nw-resize', isCorner: true },
    { name: 'n', top: '-5px', left: '50%', transform: 'translateX(-50%)', cursor: 'n-resize', style: horizontalEdgeHandleStyle },
    { name: 'ne', top: '-5px', right: '-5px', cursor: 'ne-resize', isCorner: true },
    { name: 'e', top: '50%', right: '-5px', transform: 'translateY(-50%)', cursor: 'e-resize', style: verticalEdgeHandleStyle },
    { name: 'se', bottom: '-5px', right: '-5px', cursor: 'se-resize', isCorner: true },
    { name: 's', bottom: '-5px', left: '50%', transform: 'translateX(-50%)', cursor: 's-resize', style: horizontalEdgeHandleStyle },
    { name: 'sw', bottom: '-5px', left: '-5px', cursor: 'sw-resize', isCorner: true },
    { name: 'w', top: '50%', left: '-5px', transform: 'translateY(-50%)', cursor: 'w-resize', style: verticalEdgeHandleStyle },
  ];

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <img
        ref={imageRef}
        id="monster-image"
        src={imageSrc}
        onMouseDown={handleImageMouseDown}
        style={{
          position: 'absolute',
          width: `${displayWidth}px`,
          bottom: `${bottom - positionDy}px`,
          right: `${right - positionDx}px`,
          zIndex: -1,
          transform: displayTransform,
          cursor: isDragging ? 'grabbing' : isFocused ? 'move' : 'pointer',
          opacity: opacity,
          userSelect: 'none',
        }}
        alt={name}
        draggable={false}
      />

      {/* Clickable overlay because the image is behind everything else*/}
      {displayHeight > 0 && (
        <div
          style={{
            position: 'fixed',
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            top: `${imageCenterViewport.y - displayHeight / 2}px`,
            left: `${imageCenterViewport.x - displayWidth / 2}px`,
            transform: `rotate(${displayRotation}deg)`,
            transformOrigin: 'center center',
            backgroundColor: 'transparent',
            cursor: isDragging ? 'grabbing' : isFocused ? 'move' : 'pointer',
            pointerEvents: (isResizing || isRotating) ? 'none' : 'auto',
            zIndex: 10, // Above everything but below controls
            clipPath,
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsFocused(true);
            setIsDragging(true);
            setStartDragPos({ x: e.clientX, y: e.clientY });
            setAccumulatedDelta({ dx: 0, dy: 0 });
            onMouseDown();
          }}
          title="Click to select and drag image"
        />
      )}

      {isFocused && !isDragging && displayHeight > 0 && (
        <div
          style={{
            position: 'fixed',
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            top: `${imageCenterViewport.y - displayHeight / 2}px`,
            left: `${imageCenterViewport.x - displayWidth / 2}px`,
            border: '1.5px solid #4285f440',
            boxSizing: 'border-box',
            pointerEvents: 'none',
            zIndex: 11,
            transform: `rotate(${displayRotation}deg)`,
            transformOrigin: 'center center',
            clipPath,
          }}
        >
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {handles.map((handle) => {
              if (handle.isCorner) {
                // Render corner as L-shape with three rectangles
                return (
                  <div
                    key={handle.name}
                    style={{
                      position: 'absolute',
                      top: handle.top,
                      left: handle.left,
                      right: handle.right,
                      bottom: handle.bottom,
                      width: '15px',
                      height: '15px',
                      pointerEvents: 'auto',
                      cursor: handle.cursor,
                    }}
                    onMouseDown={(e) => handleResizeMouseDown(e, handle.name)}
                  >
                    {/* Horizontal bar */}
                    <div style={{
                      position: 'absolute',
                      width: '15px',
                      height: '4px',
                      backgroundColor: '#4285f4',
                      borderRadius: '5px',
                      ...(handle.name.includes('n') ? {
                        top: 0,
                        borderTop: '1px solid white',
                      } : {
                        bottom: 0,
                        borderBottom: '1px solid white',
                      }),
                      ...(handle.name.includes('w') ? {
                        left: 0,
                        borderLeft: '1px solid white',
                        borderRight: '1px solid white',
                      } : {
                        right: 0,
                        borderLeft: '1px solid white',
                        borderRight: '1px solid white',
                      }),
                    }} />
                    {/* Vertical bar */}
                    <div style={{
                      position: 'absolute',
                      width: '4px',
                      height: '15px',
                      backgroundColor: '#4285f4',
                      borderRadius: '5px',
                      ...(handle.name.includes('n') ? {
                        top: 0,
                        borderTop: '1px solid white',
                        borderBottom: '1px solid white',
                      } : {
                        bottom: 0,
                        borderTop: '1px solid white',
                        borderBottom: '1px solid white',
                      }),
                      ...(handle.name.includes('w') ? {
                        left: 0,
                        borderLeft: '1px solid white',
                      } : {
                        right: 0,
                        borderRight: '1px solid white',
                      }),
                    }} />
                    {/* Corner square (on top, borders on outer edges only) */}
                    <div style={{
                      position: 'absolute',
                      width: '4px',
                      height: '4px',
                      backgroundColor: '#4285f4',
                      zIndex: 1,
                      ...(handle.name.includes('n') ? {
                        top: 0,
                        borderTop: '1px solid white',
                      } : {
                        bottom: 0,
                        borderBottom: '1px solid white',
                      }),
                      ...(handle.name.includes('w') ? {
                        left: 0,
                        borderLeft: '1px solid white',
                      } : {
                        right: 0,
                        borderRight: '1px solid white',
                      }),
                    }} />
                  </div>
                );
              } else {
                // Render edge handle as single rectangle
                return (
                  <div
                    key={handle.name}
                    style={{
                      ...handle.style,
                      top: handle.top,
                      left: handle.left,
                      right: handle.right,
                      bottom: handle.bottom,
                      transform: handle.transform,
                      cursor: handle.cursor,
                    }}
                    onMouseDown={(e) => handleResizeMouseDown(e, handle.name)}
                  />
                );
              }
            })}
          </div>
        </div>
      )}

      {/* Rotation handle - circle above the image (rotates with image) */}
      {isFocused && !isDragging && displayHeight > 0 && (() => {
        // Calculate rotated position of the handle
        const distanceFromCenter = displayHeight / 2 + 20;
        const rotationRad = (displayRotation * Math.PI) / 180;

        // Handle starts at 90° (straight up), rotates with image
        const handleOffsetX = distanceFromCenter * Math.sin(rotationRad);
        const handleOffsetY = -distanceFromCenter * Math.cos(rotationRad); // Negative for screen coordinates

        return (
          <div
            style={{
              position: 'fixed',
              width: '15px',
              height: '15px',
              borderRadius: '50%',
              backgroundColor: '#4285f4',
              border: '2px solid white',
              top: `${imageCenterViewport.y + handleOffsetY - 10}px`,
              left: `${imageCenterViewport.x + handleOffsetX - 10}px`,
              cursor: rotationCursor,
              pointerEvents: 'auto',
              zIndex: 12,
            }}
            onMouseDown={handleRotateMouseDown}
          />
        );
      })()}
    </div>
  );
};

export default DraggableImage;
